/**
 * FormOverlay Component
 *
 * Generic fullscreen overlay portal for forms.
 * Handles portal creation, body scroll locking, and animations.
 * Can optionally animate towards a specific element on close.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';

export interface FormOverlayProps {
  /** Whether the overlay is visible */
  isOpen: boolean;
  /** Callback fired when the overlay is closed */
  onClose: () => void;
  /** Optional CSS selector for element to animate towards on close (e.g., "[data-reports-icon]") */
  animateTowardsSelector?: string;
  /** The form content to render inside the overlay */
  children: React.ReactNode;
}

export const FormOverlay: React.FC<FormOverlayProps> = ({
  isOpen,
  onClose,
  animateTowardsSelector,
  children,
}) => {
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0 });

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-scroll-locked');
    } else {
      document.body.classList.remove('body-scroll-locked');
    }

    return () => {
      document.body.classList.remove('body-scroll-locked');
    };
  }, [isOpen]);

  // Get target element position when animating towards it
  useEffect(() => {
    if (animateTowardsSelector) {
      const targetElement = document.querySelector(animateTowardsSelector);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetPosition({
          x: rect.left + rect.width / 2 - window.innerWidth / 2,
          y: rect.top + rect.height / 2 - window.innerHeight / 2,
        });
      }
    }
  }, [animateTowardsSelector]);

  // Note: current call sites conditionally mount this component via AnimatePresence,
  // so isOpen is always true when mounted. If you change the usage pattern to keep
  // this mounted and toggle isOpen, you must also wrap the portal content in its own
  // AnimatePresence — otherwise the exit animation will be skipped.
  if (!isOpen) {
    return null;
  }

  return createPortal(
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
      exit={
        animateTowardsSelector
          ? {
              opacity: 0,
              scale: 0.3,
              x: targetPosition.x,
              y: targetPosition.y,
            }
          : { opacity: 0, scale: 0.95 }
      }
      transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1],
      }}
      className="fixed inset-0 z-50"
      onClick={onClose}
    >
      {children}
    </motion.div>,
    document.body
  );
};
